import React, {
  ChangeEvent,
  CompositionEventHandler,
  HTMLInputTypeAttribute,
  KeyboardEvent,
  MouseEventHandler,
  ReactNode,
  useState
} from 'react'

import clsx from 'clsx'

// import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
// import IconButton from '@/components/view/atoms/button/IconButton'
import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import Text from '@/components/view/atoms/text/Text'

// import Text from '@/components/view/atoms/text/Text'

const NORMAL_STRING_MAX_LENGTH = 255

export const FieldType = {
  DEFAULT: 'default',
  SEMI_REQUIRED: 'semi-required',
  REQUIRED: 'required'
} as const

export type FieldType = (typeof FieldType)[keyof typeof FieldType]

export const TextBoxSize = {
  X_SMALL: 'x-small',
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
  FULL: 'full',
  FIT: 'fit',
  HALF: 'half',
  REMNANT: 'remnant'
} as const

export type TextBoxSize = (typeof TextBoxSize)[keyof typeof TextBoxSize]

interface Props {
  id?: string
  fieldType?: FieldType
  name?: string
  type?: HTMLInputTypeAttribute
  value?: string | number
  onChange(event: ChangeEvent<HTMLInputElement>): void
  onKeyDown?(event: KeyboardEvent<HTMLInputElement>): void
  onBlur?(event: ChangeEvent<HTMLInputElement>): void
  onFocus?(event: ChangeEvent<HTMLInputElement>): void
  onCompositionStart?: CompositionEventHandler<HTMLInputElement>
  onCompositionEnd?: CompositionEventHandler<HTMLInputElement>
  maxLength?: number
  minLength?: number
  placeholder?: string
  autoComplete?: string
  error?: boolean
  required?: boolean
  disabled?: boolean
  min?: string | number
  max?: string | number
  pattern?: string
  size?: TextBoxSize
  autoFocus?: boolean
  inputRef?: React.LegacyRef<HTMLInputElement>
  prefix?: ReactNode
  suffix?: ReactNode
  radioIcon?: boolean
  suffixNode?: ReactNode
  badgeText?: string
  onClickBadgeClose?: MouseEventHandler<HTMLButtonElement>
  onClickDelete?: MouseEventHandler<HTMLButtonElement>
  inversion?: boolean
  shrink0?: boolean
  label?: string
  manage?: boolean
  disabledNoOpacity?: boolean
}

const TextField = ({
  id,
  fieldType = FieldType.DEFAULT,
  name,
  size = TextBoxSize.MEDIUM,
  type = 'text',
  value,
  onChange,
  onKeyDown,
  onBlur,
  onFocus,
  onCompositionStart,
  onCompositionEnd,
  maxLength = NORMAL_STRING_MAX_LENGTH,
  minLength,
  placeholder = '',
  autoComplete = 'off',
  error = false,
  required = false,
  disabled = false,
  min,
  max,
  pattern,
  autoFocus = false,
  inputRef,
  prefix,
  suffix,
  radioIcon = false,
  suffixNode,
  badgeText,
  onClickBadgeClose,
  onClickDelete = undefined,
  inversion = false,
  shrink0 = false,
  label,
  manage = false,
  disabledNoOpacity = false
}: Props) => {
  const widthClass =
    size == TextBoxSize.X_SMALL
      ? 'w-16 max-w-16'
      : size == TextBoxSize.SMALL
        ? 'w-22 max-w-22'
        : size == TextBoxSize.MEDIUM
          ? 'w-46 max-w-46'
          : size == TextBoxSize.LARGE
            ? 'w-full max-w-[28rem]'
            : size == TextBoxSize.FIT
              ? 'w-fit'
              : size == TextBoxSize.HALF
                ? 'w-[50%]'
                : size == TextBoxSize.REMNANT
                  ? 'flex-1'
                  : 'w-full'

  const hoverActiveClass = inversion
    ? 'hover:bg-fill-brand-on-bg-fill-hover active:bg-fill-brand-on-bg-fill-active'
    : !badgeText &&
      'hover:bg-surface-hover hover:border-border-input-hover active:bg-surface-active active:border-border-input-active'

  const bgClass = inversion
    ? 'bg-fill-brand-on-bg-fill'
    : error
      ? 'bg-surface-critical'
      : disabled
        ? 'bg-surface-disabled pointer-events-none'
        : fieldType == FieldType.REQUIRED
          ? 'has-[input:placeholder-shown]:bg-surface-critical bg-surface has-[input[type=date]:required:invalid]:bg-surface-critical'
          : 'bg-surface'

  const placeholderShownClass =
    (fieldType == FieldType.REQUIRED || fieldType == FieldType.SEMI_REQUIRED) &&
    'peer-placeholder-shown:border-critical-secondary border-critical'

  const labelPlaceholderShownClass =
    (fieldType == FieldType.REQUIRED || fieldType == FieldType.SEMI_REQUIRED) &&
    'peer-placeholder-shown:border-critical-secondary border-critical'

  const borderColorClass = error
    ? 'border-critical-secondary'
    : disabled
      ? 'border-none'
      : 'border-border-input'

  // password
  const [passwordType, setPasswordType] = useState('password')
  const toggleType = passwordType == 'password' ? 'text' : 'password'
  const toggleIcon = passwordType == 'password' ? IconType.VISIBILITY_OFF : IconType.VISIBILITY

  return (
    <>
      <div
        id={id}
        className={clsx(
          bgClass,
          hoverActiveClass,
          `${widthClass} flex1 text-size-13 relative inline-flex h-8 max-h-8 items-center justify-between rounded-lg`,
          !badgeText && 'group',
          size == TextBoxSize.X_SMALL ? 'gap-x-1' : 'gap-x-2',
          shrink0 ? 'shrink-0' : 'shrink'
        )}
      >
        {prefix && (
          <span
            className={clsx(
              'bg-surface-transparent text-secondary pointer-events-none inline-block pl-1.5'
            )}
          >
            {prefix}
          </span>
        )}
        {/* {radioIcon && (
          <span className={'pointer-events-none flex items-center pl-2'}>
            <Icon color={IconColor.DISABLED} hoverColor={IconColor.DISABLED} activeColor={IconColor.DISABLED} type={IconType.RADIO_BUTTON} />
          </span>
        )} */}
        {type == 'search' && (
          <span className={'pointer-events-none pl-2.5'}>
            <Icon
              color={inversion ? IconColor.ON_COLOR : IconColor.PRIMARY}
              hoverColor={inversion ? IconColor.ON_COLOR : IconColor.PRIMARY}
              activeColor={inversion ? IconColor.ON_COLOR : IconColor.PRIMARY}
              type={IconType.SEARCH}
            />
          </span>
        )}
        <input
          name={name}
          type={type == 'password' ? passwordType : type}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          onFocus={onFocus}
          onCompositionStart={onCompositionStart}
          onCompositionEnd={onCompositionEnd}
          maxLength={maxLength}
          minLength={minLength}
          placeholder={!badgeText ? placeholder : ''}
          autoComplete={autoComplete}
          min={min}
          max={max}
          pattern={pattern}
          className={clsx(
            `bg-surface-transparent text-size-13 placeholder:text-placeholder peer h-full w-full appearance-none truncate py-1.5 leading-15 focus:appearance-none focus:outline-none`,
            prefix || radioIcon || type == 'search' ? 'pr-2' : type == 'number' ? 'pl-2' : 'px-2',
            badgeText && 'sr-only',
            disabled && !value && (type == 'date' || type == 'datetime-local')
              ? 'text-placeholder'
              : 'text-primary'
          )}
          required={required}
          disabled={disabled}
          autoFocus={autoFocus}
          ref={inputRef}
          step={type == 'number' ? 'any' : undefined}
        />
        {/* {badgeText && (
          <div className='block pl-2'>
            <Badge
              badgeType={manage ? BadgeType.TAG_EMPHASIS : BadgeType.DEFAULT}
              onClickClose={disabled ? undefined : onClickBadgeClose}
            >
              {badgeText}
            </Badge>
          </div>
        )} */}
        {suffix && (
          <span
            className={clsx('bg-surface-transparent text-secondary inline-block shrink-0 pr-2')}
          >
            {suffix}
          </span>
        )}
        {type == 'password' && (
          <span className={'cursor-pointer pr-2'} onClick={() => setPasswordType(toggleType)}>
            <Icon
              color={IconColor.PRIMARY}
              hoverColor={IconColor.PRIMARY}
              activeColor={IconColor.PRIMARY}
              type={toggleIcon}
            />
          </span>
        )}
        {suffixNode && <>{suffixNode}</>}
        {label ? (
          <span
            className={clsx(
              'outline-default-peer pointer-events-none absolute inset-0 rounded-lg',
              shrink0 ? 'shrink-0' : 'shrink',
              'flex',
              (fieldType == FieldType.REQUIRED || fieldType == FieldType.SEMI_REQUIRED) && 'sibling'
            )}
          >
            <span
              className={clsx(
                'w-1 max-w-4 min-w-4 rounded-l-lg border-y border-l',
                borderColorClass,
                placeholderShownClass
              )}
            ></span>
            <span
              className={clsx(
                '-mt-1.5 h-[calc(100%+6px)] w-fit max-w-[calc(100%-32px)] border-b px-1 whitespace-nowrap',
                borderColorClass,
                placeholderShownClass
              )}
            >
              <Text size={10}>{label}</Text>
            </span>
            <span
              className={clsx(
                'flex-1 rounded-r-lg border-y border-r',
                borderColorClass,
                placeholderShownClass
              )}
            ></span>
          </span>
        ) : (
          <span
            className={clsx(
              'outline-default-peer pointer-events-none absolute inset-0 rounded-lg border',
              shrink0 ? 'shrink-0' : 'shrink',
              borderColorClass,
              placeholderShownClass
            )}
          ></span>
        )}
        {/* {onClickDelete && (
          <div className='pointer-events-auto block pr-0.5'>
            <IconButton type={IconType.CLOSE} color={IconColor.PRIMARY} onClick={onClickDelete} />
          </div>
        )} */}
      </div>
    </>
  )
}

export default TextField
